import React, { useState } from 'react';
import c from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { getDeviationType } from '../../DeviationViews/_helpers';
import { useSubscriptionTier } from '../../utils/hooks/useSubscriptionTier';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import ArtStage from '../../ArtStage';
import MonetizationBanner from '../../banners/MonetizationBanner';
import ActionBar from '../../ActionBar';
import Sidebar from '../../Sidebar';
import Details from '../../DeviationViews/Details';
import SubPaywall from '../../DeviationViews/Full/_partials/SubPaywall';
import { DeviationTypes } from '../../types/deviation';
import {
  getCommentableTypeForDeviationType,
  isExtendedDeviationFullyLoaded,
} from '../redux/helpers';
import Comments from '../_partials/Comments';
import TierUpsell from '../_partials/TierUpsell';
import UserGiftSaleBanner from '../_partials/UserGiftSaleBanner';
import LiteratureTextualContent from '../../DeviationViews/TextualContent/Literature';
import AdoptableInfo from '../../Sidebar/SidebarBlock/layouts/AdoptableInfo/AdoptableInfo';
import PcpInfo from '../../Sidebar/SidebarBlock/layouts/PcpInfo/PcpInfo';
import MonetizationBlock from '../../Sidebar/SidebarBlock/layouts/MonetizationBlock';
import OriginalInfo from '../../Sidebar/SidebarBlock/layouts/OriginalInfo/OriginalInfo';
import MoreOriginals from '../../Sidebar/SidebarBlock/layouts/MoreOriginals/MoreOriginals';
import OriginalSource from '../../Sidebar/SidebarBlock/layouts/OriginalSource';
import GruserZoneLocal from '../../DeviationViews/GruserZone/Local';

import s from './MobileDeviationPage.scss';

export interface Props {
  deviation: PapiDeviation;
  withoutSidebar?: boolean;
}

const MobileDeviationPage: React.FC<Props> = ({
  deviation,
  withoutSidebar,
}) => {
  const withSidebar = !withoutSidebar;
  const isExtendedLoaded = isExtendedDeviationFullyLoaded(deviation);

  const deviationType = getDeviationType(deviation);
  const isJournal = deviationType === DeviationTypes.JOURNAL;
  const isLiterature = deviationType === DeviationTypes.LITERATURE;
  const isJournalOrLit = isJournal || isLiterature;

  const { deviationId } = deviation;
  const commentableTypeId = getCommentableTypeForDeviationType(deviation);

  const { tier, isSubscribedButBlocked, needsSubscription } =
    useSubscriptionTier(deviation);
  const isBlocked = needsSubscription || isSubscribedButBlocked;

  // we'll force a re-render of the page when a countdown in the offer banner of the monetization strip
  // reaches 0 (onAdoptableSaleEnd). This will update the actionbar to change from the 'make an offer'
  // button to the 'view offers' button to show the sale is over
  const [hasAdoptionEnded, setHasAdoptionEnded] = useState(false);

  // TODO: Replace these hacks with a better approach to having sidebar blocks in a different zone
  const monetizationBlock = deviation.extended?.relatedContent?.find(
    block =>
      block.contentType.includes('monetization_') ||
      block.contentType === 'monetization_promotion_adoptable'
  );

  const hasAdopts = !!deviation.extended?.adoptable;

  const hasPcp = deviation.pcp && deviation.extended?.pcp;
  const showPcpInfoBlock = !!deviation.extended?.relatedContent?.find(
    block => block.contentType === 'pcp_info'
  );

  const hasOriginals = !!deviation.extended?.originals;
  const moreOriginalsBlock = deviation.extended?.relatedContent?.find(
    block => block.contentType === 'other_originals'
  );
  const monetizationBlockIsOriginalSource =
    monetizationBlock?.contentType ===
    'monetization_promotion_originals_firstparty';

  return (
    <>
      <div className={s['root']}>
        <ArtStage deviation={deviation} />
        <MonetizationBanner
          deviation={deviation}
          onAdoptableSaleEnd={() => setHasAdoptionEnded(true)}
        />
        <ActionBar
          // maintain a key so we can force a re-render
          key={`actionbar-${hasAdoptionEnded}`}
          deviation={deviation}
        />
        {hasAdopts && <AdoptableInfo deviation={deviation} />}
        {hasPcp && (
          <PcpInfo className={s['upper-pcp-info']} deviation={deviation} />
        )}
        {hasOriginals && <OriginalInfo deviation={deviation} />}

        {isLiterature && (
          <div className={s['lit-content']}>
            <LiteratureTextualContent deviation={deviation} />
          </div>
        )}

        <Details contentClassName={s['details']} deviation={deviation} />
        <UserGiftSaleBanner user={deviation.author} />

        {/* only show wall for j/l because others have wall in artstage */}
        {isJournalOrLit && isBlocked && (
          <SubPaywall deviation={deviation} showImage={false} />
        )}

        {showPcpInfoBlock && (
          <PcpInfo
            className={c(
              isExtendedLoaded && monetizationBlock && s['bottom-gap']
            )}
            deviation={deviation}
          />
        )}

        {hasOriginals && moreOriginalsBlock && (
          <MoreOriginals
            className={s['more-originals']}
            block={moreOriginalsBlock}
            deviation={deviation}
          />
        )}

        {isExtendedLoaded ? (
          <>
            {monetizationBlock && (
              <BiLoggerContextProvider
                value={{
                  sectionname: monetizationBlock.contentType,
                }}
              >
                {monetizationBlockIsOriginalSource ? (
                  <div className={s['original-source-wrapper']}>
                    <OriginalSource block={monetizationBlock} />
                  </div>
                ) : (
                  <MonetizationBlock
                    block={monetizationBlock}
                    deviation={deviation}
                  />
                )}
              </BiLoggerContextProvider>
            )}
            <div className={s['gruser-wrapper']}>
              <GruserZoneLocal deviation={deviation} />
            </div>
            {!isBlocked && (
              <Comments
                key={`${commentableTypeId}:${deviationId}`}
                deviation={deviation}
                className={s['comments']}
              />
            )}
            {/* note: we -don't- show the tier upsell when isSubscribedButBlocked on desktop
          but do show it when isSubscribedButBlocked on mobile */}
            {/* when subscribed/owner put the upsell before the sidebar */}
            {!isBlocked && !!tier && (
              <TierUpsell className={s['upsell']} deviation={deviation} />
            )}
            {withSidebar && (
              <div className={s['sidebar']}>
                <Sidebar deviation={deviation} />
              </div>
            )}
          </>
        ) : (
          <LoadingIndicator className={s['lloader']} view="llama" />
        )}

        {/* when unsubscribed put the upsell after the sidebar */}
        {isBlocked && !!tier && (
          <TierUpsell className={s['upsell']} deviation={deviation} />
        )}
      </div>
    </>
  );
};
MobileDeviationPage.displayName = 'MobileDeviationPage';

export default MobileDeviationPage;
